import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

export default function PlantZoomView({ selectedPlant, plants, currentSnapshot, snapshotImageRef }) {
    const canvasRef = useRef(null);
    
    // Find the selected plant
    const plant = selectedPlant ? plants.find(p => p.id === selectedPlant) : null;
    
    // Extract and draw zoomed bounding box region
    useEffect(() => {
        // Only run if we have all required data
        if (!canvasRef.current || !currentSnapshot?.imageUrl || !plant?.boundingBox) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const { x, y, width, height } = plant.boundingBox;
            
            // Calculate scale between displayed and natural image dimensions
            let scaleX = 1;
            let scaleY = 1;
            
            if (snapshotImageRef?.current) {
                // Get the displayed dimensions
                const displayedWidth = snapshotImageRef.current.clientWidth;
                const displayedHeight = snapshotImageRef.current.clientHeight;
                
                // Get the natural (original) dimensions
                const naturalWidth = img.naturalWidth || img.width;
                const naturalHeight = img.naturalHeight || img.height;
                
                // Calculate scale factors
                scaleX = naturalWidth / displayedWidth;
                scaleY = naturalHeight / displayedHeight;
                
                console.log('Scale factors:', { scaleX, scaleY, displayedWidth, displayedHeight, naturalWidth, naturalHeight });
            }
            
            // Apply scale to bounding box coordinates
            const scaledX = x * scaleX;
            const scaledY = y * scaleY;
            const scaledWidth = width * scaleX;
            const scaledHeight = height * scaleY;
            
            // Add padding around the crop for context
            const padding = 40 * Math.max(scaleX, scaleY);  // Scale padding too
            const cropX = Math.max(0, scaledX - padding);
            const cropY = Math.max(0, scaledY - padding);
            const cropWidth = Math.min(scaledWidth + (padding * 2), img.width - cropX);
            const cropHeight = Math.min(scaledHeight + (padding * 2), img.height - cropY);
            
            // Set the actual canvas pixel dimensions to match the crop area
            canvas.width = cropWidth;
            canvas.height = cropHeight;
            
            // Clear canvas first
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Draw the cropped region from the source image
            ctx.drawImage(
                img,
                cropX, cropY, cropWidth, cropHeight,  // Source rectangle from original image
                0, 0, cropWidth, cropHeight           // Destination rectangle on canvas
            );
            
            // Draw a neon border around the actual bounding box area (without padding)
            ctx.strokeStyle = '#FF1493';  // Neon pink to match selected state
            ctx.lineWidth = 4;
            ctx.shadowColor = '#FF1493';
            ctx.shadowBlur = 8;
            const boxX = scaledX - cropX;  // Position within the cropped area
            const boxY = scaledY - cropY;
            ctx.strokeRect(boxX, boxY, scaledWidth, scaledHeight);
        };
        
        img.onerror = (error) => {
            console.error('Error loading image for zoom view:', error);
        };
        
        img.src = currentSnapshot.imageUrl;
    }, [plant, currentSnapshot, snapshotImageRef]);

    // Don't render anything if no plant is selected
    if (!selectedPlant || !plant) {
        return null;
    }

    return (
        <div className="plant-zoom-view">
            <div className="zoom-view-header">
                <i className="ri-search-eye-line"></i>
                <span>Close-up View: {plant.name}</span>
            </div>
            <div className="zoom-canvas-container">
                <canvas 
                    ref={canvasRef}
                    className="plant-zoom-canvas"
                />
            </div>
        </div>
    );
}

PlantZoomView.propTypes = {
    selectedPlant: PropTypes.string,
    plants: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        boundingBox: PropTypes.shape({
            x: PropTypes.number.isRequired,
            y: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired
        }).isRequired
    })).isRequired,
    currentSnapshot: PropTypes.shape({
        id: PropTypes.string,
        imageUrl: PropTypes.string,
        timestamp: PropTypes.string
    }),
    snapshotImageRef: PropTypes.shape({
        current: PropTypes.instanceOf(Element)
    })
};

